import React, { useState, useEffect } from "react";
import {
  Database,
  Users,
  LayoutGrid,
  Network,
  Share2,
  CloudUpload,
  ArrowRight,
} from "lucide-react";
import { heroBg } from "../../assets";
import { useLanguage } from "../../contexts/LanguageContext";
import { useSiteInfo } from "../../hooks/useSiteInfo";
import { useContentBlocks } from "../../hooks/useContentBlocks";
import { pickBlock } from "../../helpers/contentHelpers";
import { resolveIcon } from "../../helpers/iconResolver";

// Default nodes used when no content blocks exist in DB
const DEFAULT_NODES = [
  { icon: <Database size={20} />, labelKey: "myCompany", x: 50, y: 12 },
  { icon: <Users size={20} />, labelKey: "myEmployees", x: 88, y: 35 },
  { icon: <LayoutGrid size={20} />, labelKey: "mySubsidiaries", x: 82, y: 75 },
  { icon: <Network size={20} />, labelKey: "suppliers", x: 50, y: 88 },
  { icon: <Share2 size={20} />, labelKey: "customers", x: 18, y: 75 },
  { icon: <CloudUpload size={20} />, labelKey: "partieRequest", x: 12, y: 35 },
];

// Default positions for dynamically-loaded nodes
const NODE_POSITIONS = [
  { x: 50, y: 12 },
  { x: 88, y: 35 },
  { x: 82, y: 75 },
  { x: 50, y: 88 },
  { x: 18, y: 75 },
  { x: 12, y: 35 },
];

const Hero = ({ sectionData }) => {
  const [scrollY, setScrollY] = useState(0);
  const { t, language } = useLanguage();
  const { info } = useSiteInfo();
  const pick = (en, ja, key) => (language === "ja" && ja) ? ja : (en ?? t(key));

  // Fetch content blocks for the hero section
  const sectionId = sectionData?.id;
  const { blocks: allBlocks } = useContentBlocks(sectionId, "hero");

  // Separate block types
  const badgeBlocks = allBlocks.filter((b) => b.blockType === "badge" || b.blockType === "trust-indicator");
  const nodeBlocks = allBlocks.filter((b) => b.blockType === "node");

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrollY(window.scrollY);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Build trust badges — from content blocks or fallback to siteInfo
  const trustBadges = badgeBlocks.length > 0
    ? badgeBlocks.map((block) => {
        const picked = pickBlock(block, language);
        return { value: picked.value, label: picked.label };
      })
    : [
        { value: info("stat_talent_placed", "1,500+"), label: t("hero.trust.placed") },
        { value: info("stat_success_rate", "94%"), label: t("hero.trust.rate") },
        { value: info("stat_partners", "450+"), label: t("hero.trust.partners") },
        { value: "★★★★★", label: t("hero.trust.license") },
      ];

  // Build diagram nodes — from content blocks or fallback to defaults
  const diagramNodes = nodeBlocks.length > 0
    ? nodeBlocks.map((block, i) => {
        const picked = pickBlock(block, language);
        const IconComp = resolveIcon(block.iconName);
        const pos = block.metadata || NODE_POSITIONS[i % NODE_POSITIONS.length];
        return {
          icon: IconComp ? <IconComp size={20} /> : <Database size={20} />,
          label: picked.label,
          x: pos.x ?? NODE_POSITIONS[i % NODE_POSITIONS.length].x,
          y: pos.y ?? NODE_POSITIONS[i % NODE_POSITIONS.length].y,
        };
      })
    : DEFAULT_NODES.map((n) => ({ ...n, label: t(`hero.nodes.${n.labelKey}`) }));

  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden bg-slate-900
                 pt-20 pb-4 md:pt-28 lg:pt-28"
    >
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-75 ease-out opacity-50"
        style={{
          backgroundImage: `url(${heroBg})`,
          transform: `translateY(${scrollY * 0.2}px) scale(1.1)`,
        }}
      />

      {/* Gradient Overlay */}
      <div className="absolute inset-0" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 w-full grid md:grid-cols-2 gap-8 lg:gap-16 items-center">
        {/* Left Side: Text Content */}
        <div className="text-center md:text-left">
          <h1 className="text-white font-bold text-4xl sm:text-5xl lg:text-7xl leading-tight mb-6">
            {pick(sectionData?.titleEn, sectionData?.titleJa, "hero.title")} <br />
            {/* <span className="text-[#9AC101]">{pick(sectionData?.subtitleEn, sectionData?.subtitleJa, "hero.subtitle")}</span> */}
          </h1>
          <p className="text-slate-200 text-lg lg:text-xl leading-relaxed mb-10 max-w-xl mx-auto md:mx-0">
            {pick(sectionData?.subtitleEn, sectionData?.subtitleJa, "hero.description")}
          </p>
          <a
            href="#benefits"
            className="bg-[#569C27] hover:bg-[#4a8421] text-white font-bold px-10 py-4 rounded-full inline-flex items-center gap-2 transition-all transform hover:scale-105 shadow-xl cursor-pointer"
          >
            {pick(sectionData?.ctaButtonEn, sectionData?.ctaButtonJa, "hero.learnMore")} <ArrowRight size={20} />
          </a>

          {/* Trust badge strip */}
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 items-center">
            {trustBadges.map((badge, i) => (
              <div key={i} className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 transition-all hover:bg-white/15">
                <span className="text-accent-green font-black text-sm">{badge.value}</span>
                <span className="text-slate-300 text-[11px] font-medium">{badge.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right Side: Responsive Diagram */}
        <div className="hidden md:flex justify-center items-center relative w-full aspect-square max-w-[500px] lg:max-w-[550px] mx-auto transform scale-90 lg:scale-100">
          {/* Connecting Lines */}
          <svg
            className="absolute inset-0 w-full h-full opacity-30"
            viewBox="0 0 100 100"
          >
            {diagramNodes.map((node, i) => (
              <line
                key={i}
                x1="50"
                y1="50"
                x2={node.x}
                y2={node.y}
                stroke="white"
                strokeWidth="0.5"
                strokeDasharray="3"
              />
            ))}
          </svg>

          {/* Central White Hub */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-white rounded-full shadow-[0_0_50px_rgba(255,255,255,0.2)] z-20 flex items-center justify-center">
            <div className="w-20 h-20 border-2 border-slate-100 rounded-full animate-pulse" />
          </div>

          {/* Satellite Feature Cards */}
          {diagramNodes.map((node, i) => (
            <div
              key={i}
              className="absolute transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center animate-float"
              style={{
                left: `${node.x}%`,
                top: `${node.y}%`,
                animationDelay: `${i * 0.5}s`,
              }}
            >
              <div className="bg-white p-3 lg:p-4 rounded-2xl shadow-2xl flex flex-col items-center min-w-[100px] lg:min-w-[120px] transition-all hover:scale-110 border border-white/50">
                <div className="text-slate-600 mb-2">{node.icon}</div>
                <span className="text-[10px] lg:text-xs font-bold text-slate-800 uppercase tracking-tight text-center">
                  {node.label}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
